import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ServiceExecutiveService } from '../../services/serviceExecutiveService';
import { AlertCircle, ClipboardList, CheckCircle, Clock, Phone, MapPin } from 'lucide-react';

const ServiceExecutiveComplaints = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const { data: complaintsData, isLoading } = useQuery({
    queryKey: ['serviceExecutiveComplaints', user?.id],
    queryFn: () => ServiceExecutiveService.getComplaints(user?.id),
    enabled: !!user?.id,
  });
  const complaints = complaintsData?.data || [];

  const filteredComplaints = filterStatus === 'all' 
    ? complaints 
    : complaints.filter(c => c.status?.toLowerCase() === filterStatus.toLowerCase());

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Complaints</h1>
          <p className="text-gray-600 mt-1">Manage service complaints</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-lg border ${
              filterStatus === 'all' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilterStatus('pending')}
            className={`px-4 py-2 rounded-lg border ${
              filterStatus === 'pending' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilterStatus('resolved')}
            className={`px-4 py-2 rounded-lg border ${
              filterStatus === 'resolved' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            Resolved
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading complaints...</p>
        </div>
      ) : filteredComplaints.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No complaints found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredComplaints.map((complaint) => (
            <div key={complaint.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">Complaint #{complaint.id?.slice(0, 8)}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      complaint.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {complaint.status || 'Pending'}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-3">{complaint.problem || complaint.description || 'No description'}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    {complaint.customer_name && (
                      <div>
                        <p className="text-gray-500">Customer</p>
                        <p className="font-medium text-gray-900">{complaint.customer_name}</p>
                      </div>
                    )}
                    {complaint.contact_number && (
                      <div>
                        <p className="text-gray-500">Contact</p>
                        <a href={`tel:${complaint.contact_number}`} className="font-medium text-emerald-600 hover:underline">
                          {complaint.contact_number}
                        </a>
                      </div>
                    )}
                    {complaint.location && (
                      <div>
                        <p className="text-gray-500">Location</p>
                        <p className="font-medium text-gray-900">{complaint.location}</p>
                      </div>
                    )}
                    {complaint.created_at && (
                      <div>
                        <p className="text-gray-500">Created</p>
                        <p className="font-medium text-gray-900">{new Date(complaint.created_at).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-2 pt-4 border-t border-gray-200">
                <button className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600">
                  View Details
                </button>
                {complaint.status !== 'resolved' && (
                  <button className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600">
                    Mark Resolved
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ServiceExecutiveOrders = () => {
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  const [filterStatus, setFilterStatus] = useState('all');
  
  const { data: ordersData, isLoading, refetch } = useQuery({
    queryKey: ['serviceExecutiveOrders', user?.id],
    queryFn: () => ServiceExecutiveService.getOrders(user?.id),
    enabled: !!user?.id,
  });
  const orders = ordersData?.data || [];

  const handleStatusUpdate = async (orderId, newStatus) => {
    const result = await ServiceExecutiveService.updateOrderStatus(orderId, newStatus);
    if (result.success) {
      alert('Order status updated!');
      refetch();
    } else {
      alert('Failed to update status: ' + result.error);
    }
  };

  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(o => o.status?.toLowerCase() === filterStatus.toLowerCase());

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-600 mt-1">Manage service orders</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-lg border ${
              filterStatus === 'all' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilterStatus('pending')}
            className={`px-4 py-2 rounded-lg border ${
              filterStatus === 'pending' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilterStatus('completed')}
            className={`px-4 py-2 rounded-lg border ${
              filterStatus === 'completed' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            Completed
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <ClipboardList size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No orders found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">#{order.id?.slice(0, 8)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{order.customer_name || 'N/A'}</td>
                  <td className="px-6 py-4 text-gray-600">{order.service_type || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      order.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {order.status || 'Pending'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">{order.created_at ? new Date(order.created_at).toLocaleDateString() : 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {order.status !== 'completed' && (
                      <button
                        onClick={() => handleStatusUpdate(order.id, 'completed')}
                        className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                      >
                        Complete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export { ServiceExecutiveComplaints, ServiceExecutiveOrders };